import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field";
import { useLayoutEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

export const REPORT_TAB_VISIBILITY_FIELD_TYPE = "reportTabVisibility" as const;

export const ACTIVE_APP_TAB_FIELD = "__activeAppTab" as const;

const nodeCallbacks = new WeakMap<Element, () => void>();
let sharedObserver: IntersectionObserver | undefined;

const getObserver = () => {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            nodeCallbacks.get(entry.target)?.();
          }
        }
      },
      { threshold: 0, rootMargin: "200px" }
    );
  }
  return sharedObserver;
};

export type ReportTabVisibilityFieldDefinition = BaseFormFieldDefinition<
  typeof REPORT_TAB_VISIBILITY_FIELD_TYPE
> & {
  appTabId: string;
  activeFieldName: string;
  onActivate?: () => void;
};

export const ReportTabVisibilityField = (
  props: ReportTabVisibilityFieldDefinition & AdditionalFormFieldProps
) => {
  const { appTabId, activeFieldName, onActivate } = props;
  const { setValue } = useFormContext();
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = getObserver();
    nodeCallbacks.set(el, () => {
      setValue(activeFieldName, appTabId, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      onActivate?.();
    });
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      nodeCallbacks.delete(el);
    };
  }, [appTabId, activeFieldName, setValue, onActivate]);

  return (
    <span
      ref={ref}
      aria-hidden
      style={{ display: "block", width: 1, height: 1 }}
    />
  );
};
